import type { SelectLabelProp } from "./system.types";
import { ResourceEnum } from "src/constants/constants.ts";
import {
  type BrevoAccountResource,
  type CustomerResource,
  type DocumentTemplateResource,
  type OwnerProfileResource,
  type ProductResource,
  type SupplierResource,
  TextFilterModeEnum,
} from "@exonn/js-sdk";
import type { AccountingAccountResource } from "@exonn/js-sdk/src/namespaces/accounting-account";
import { doesntContainOnlyNumbers } from "@exonn/utils";
import type { SelectSearchField } from "src/components/Form/Form.types.ts";
import {
  isValidNumber,
  isValidText,
} from "src/admin/forms/accounting-posting/utils/isValid.ts";
import { GridLogicOperator } from "@mui/x-data-grid";

type Options = {
  labelProp?: SelectLabelProp<any>;
  searchField?: SelectSearchField | { (search: string): SelectSearchField };
  logicOperator?: GridLogicOperator;
  include?: string[];
};

type Props = {
  resource: string;
};

export const getDefaultSelectOptions = ({ resource }: Props): Options => {
  const defaultLabelProp: Partial<Record<string, Options>> = {
    [ResourceEnum.Customer]: {
      labelProp: (resource: CustomerResource) =>
        resource.number + " " + resource.name,
      searchField: (search) => {
        return [
          ...(isValidNumber(search)
            ? [
                {
                  field: "number",
                  operator: "exact",
                  value: Number(search),
                },
              ]
            : []),
          ...(isValidText(search)
            ? [
                {
                  field: "name",
                  operator: "partial",
                  value: search,
                },
              ]
            : []),
        ];
      },
    },
    [ResourceEnum.Owner]: {
      labelProp: "company_name",
    },
    [ResourceEnum.B2bGateSupplier]: {
      labelProp: "company",
    },
    [ResourceEnum.BrevoAccount]: {
      searchField: ["sender_name", "sender_email"],
      logicOperator: GridLogicOperator.Or,
      labelProp: (resource: BrevoAccountResource) => {
        return resource.sender_name + " (" + resource.sender_email + ")";
      },
    },
    [ResourceEnum.DocumentTemplate]: {
      labelProp: (resource: DocumentTemplateResource) => resource.name,
    },
    [ResourceEnum.Currency]: {
      labelProp: "iso",
    },
    [ResourceEnum.OwnerProfile]: {
      include: ["owner"],
      labelProp: (resource: OwnerProfileResource) =>
        resource.owner?.company_name + " => " + resource.title,
    },
    [ResourceEnum.MarketingCompany]: {
      labelProp: "name",
    },
    [ResourceEnum.BankAccount]: {
      labelProp: "name",
    },
    [ResourceEnum.SupplierOrder]: {
      include: ["owner", "supplierOrderPositions.supplier"],
      labelProp: "order_number",
      logicOperator: GridLogicOperator.Or,
      searchField: [{ field: "order_number", operator: "exact" }],
    },
    [ResourceEnum.Product]: {
      logicOperator: GridLogicOperator.Or,
      labelProp: (resource: ProductResource) => {
        return [resource.sku, resource.title].filter(Boolean).join(" | ");
      },
      searchField: (search: string) => {
        const filters = [
          {
            field: "sku",
            operator: TextFilterModeEnum.Exact,
          },
        ];
        if (doesntContainOnlyNumbers(search)) {
          filters.push({
            field: "title",
            operator: TextFilterModeEnum.Contains,
          });
        }
        return filters;
      },
    },
    [ResourceEnum.Country]: {
      labelProp: "name",
      searchField: "name",
      logicOperator: GridLogicOperator.Or,
    },
    [ResourceEnum.Supplier]: {
      labelProp: (resource: SupplierResource) =>
        resource.number + " " + resource.name,
      searchField: (search) => {
        return [
          ...(isValidNumber(search)
            ? [
                {
                  field: "number",
                  operator: "exact",
                  value: Number(search),
                },
              ]
            : []),
          ...(isValidText(search)
            ? [
                {
                  field: "name",
                  operator: "partial",
                  value: search,
                },
              ]
            : []),
        ];
      },
    },
    [ResourceEnum.AccountingAccount]: {
      labelProp: (resource: AccountingAccountResource) =>
        resource.account_number + " " + resource.title,
      searchField: (search: string) => {
        if (doesntContainOnlyNumbers(search)) {
          return [
            {
              field: "title",
              operator: TextFilterModeEnum.Contains,
              value: search,
            },
          ];
        }
        return [
          {
            field: "account_number",
            operator: TextFilterModeEnum.Exact,
            value: search,
          },
        ];
      },
    },
  };

  return {
    ...defaultLabelProp?.[resource],
  };
};
